import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import {
  ChevronLeft,
  ChevronRight,
  Expand,
  ImageOff,
} from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import ProductImage, {
  PRODUCT_DETAIL_IMAGE_SIZES,
} from "./ProductImage";
import { buildProductImageLightboxSlide } from "../../utils/productImages";

import "yet-another-react-lightbox/styles.css";
import "./ImageZoom.css";

const FALLBACK_IMAGE =
  "https://placehold.co/1200x1400?text=Academy+Costumes+London";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function getImageUrl(item) {
  if (typeof item === "string") {
    return safeText(item);
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  return safeText(
    item.url ||
      item.src ||
      item.imageUrl ||
      item.image ||
      item.secure_url ||
      item.secureUrl ||
      item.defaultImage ||
      item.defaultImageUrl ||
      item.featuredImage ||
      item.featuredImageUrl ||
      item.thumbnail ||
      item.thumbnailUrl ||
      item.mainImage ||
      item.mainImageUrl ||
      item.coverImage ||
      item.coverImageUrl ||
      item.primaryImage ||
      item.primaryImageUrl ||
      item.assetUrl ||
      item.path ||
      "",
  );
}

function normalizeImage(item, fallbackAlt = "") {
  const url = getImageUrl(item);

  if (!url) {
    return null;
  }

  const alt =
    typeof item === "string"
      ? safeText(fallbackAlt)
      : safeText(
          item.alt ||
            item.title ||
            item.name ||
            fallbackAlt,
        );

  const caption =
    typeof item === "string"
      ? ""
      : safeText(
          item.caption ||
            item.description,
        );

  return {
    ...(typeof item === "string"
      ? {}
      : item),

    url,
    src: url,
    image: url,
    imageUrl: url,
    alt,
    caption,
  };
}

function normalizeImages(
  images = [],
  fallbackAlt = "",
) {
  const seen = new Set();
  const output = [];

  (
    Array.isArray(images)
      ? images
      : []
  ).forEach((item) => {
    const normalized =
      normalizeImage(
        item,
        fallbackAlt,
      );

    const url = safeText(
      normalized?.url,
    );

    if (
      !url ||
      seen.has(url)
    ) {
      return;
    }

    seen.add(url);
    output.push(normalized);
  });

  return output;
}

function clampIndex(
  index,
  length,
) {
  if (!length) {
    return 0;
  }

  return Math.min(
    Math.max(
      0,
      Number(index) || 0,
    ),
    length - 1,
  );
}

function buildSlides(
  gallery = [],
  productName = "",
) {
  return gallery.map(
    (image) =>
      buildProductImageLightboxSlide(
        image,
        {
          alt:
            image.alt ||
            productName ||
            "Product image",
          description:
            image.caption || "",
        },
      ) || {
        src: FALLBACK_IMAGE,
        alt:
          productName ||
          "Product image",
        description: "",
      },
  );
}

const PremiumImage = memo(
  function PremiumImage({
    image,
    alt,
    caption,
    productName,
    onOpen,
    onLoad,
    isPrimary,
  }) {
    const [
      failed,
      setFailed,
    ] = useState(false);

    useEffect(() => {
      setFailed(false);
    }, [image?.url]);

    const handleError =
      useCallback(() => {
        setFailed(true);
      }, []);

    return (
      <button
        type="button"
        className="pdp-gallery__image-button"
        onClick={onOpen}
        aria-label={
          alt ||
          "Open product image"
        }
      >
        {!failed ? (
          <ProductImage
            key={
              image?.url ||
              FALLBACK_IMAGE
            }
            image={image}
            fallback={
              FALLBACK_IMAGE
            }
            alt={
              alt ||
              productName ||
              "Product image"
            }
            className="pdp-gallery__image"
            pictureClassName="pdp-gallery__picture"
            sizes={
              PRODUCT_DETAIL_IMAGE_SIZES
            }
            onError={
              handleError
            }
            onLoad={onLoad}
            loading={
              isPrimary
                ? "eager"
                : "lazy"
            }
            decoding="async"
            fetchpriority={
              isPrimary
                ? "high"
                : undefined
            }
          />
        ) : (
          <div className="pdp-gallery__image-fallback">
            <ImageOff
              size={30}
            />

            <span>
              {caption ||
                "Image unavailable"}
            </span>
          </div>
        )}

        <span
          className="pdp-gallery__image-overlay"
          aria-hidden="true"
        >
          View larger
        </span>
      </button>
    );
  },
);

export default function ImageZoom({
  images = [],
  productName = "",
  selectedImageIndex,
  onImageChange,
}) {
  const gallery = useMemo(
    () => {
      const normalized =
        normalizeImages(
          images,
          productName,
        );

      return normalized.length
        ? normalized
        : [
            {
              url:
                FALLBACK_IMAGE,

              src:
                FALLBACK_IMAGE,

              image:
                FALLBACK_IMAGE,

              imageUrl:
                FALLBACK_IMAGE,

              alt:
                productName ||
                "Product image",

              caption: "",
            },
          ];
    },
    [
      images,
      productName,
    ],
  );

  const gallerySignature =
    useMemo(
      () =>
        gallery
          .map(
            (image) =>
              image.url,
          )
          .join("|"),

      [gallery],
    );

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(() =>
    clampIndex(
      selectedImageIndex ??
        0,
      gallery.length,
    ),
  );

  const [
    lightboxOpen,
    setLightboxOpen,
  ] = useState(false);

  const [
    hasLoadedMainImage,
    setHasLoadedMainImage,
  ] = useState(false);

  const thumbRailRef =
    useRef(null);

  const canNavigate =
    gallery.length > 1;

  const safeActiveIndex =
    clampIndex(
      activeIndex,
      gallery.length,
    );

  const activeImage =
    gallery[
      safeActiveIndex
    ] || gallery[0];

  const slides = useMemo(
    () =>
      buildSlides(
        gallery,
        productName,
      ),

    [
      gallery,
      productName,
    ],
  );

  const setActiveIndexSafely =
    useCallback(
      (
        nextIndex,
        {
          notify = true,
        } = {},
      ) => {
        const clampedIndex =
          clampIndex(
            nextIndex,
            gallery.length,
          );

        setActiveIndex(
          (
            previousIndex,
          ) =>
            previousIndex ===
            clampedIndex
              ? previousIndex
              : clampedIndex,
        );

        if (
          notify &&
          typeof onImageChange ===
            "function"
        ) {
          onImageChange(
            clampedIndex,
          );
        }
      },

      [
        gallery.length,
        onImageChange,
      ],
    );

  useEffect(() => {
    if (
      selectedImageIndex ===
        undefined ||
      selectedImageIndex ===
        null
    ) {
      return;
    }

    setActiveIndexSafely(
      selectedImageIndex,
      {
        notify: false,
      },
    );
  }, [
    selectedImageIndex,
    setActiveIndexSafely,
  ]);

  useEffect(() => {
    setActiveIndex(
      (
        previousIndex,
      ) =>
        clampIndex(
          previousIndex,
          gallery.length,
        ),
    );

    setHasLoadedMainImage(
      false,
    );
  }, [
    gallery.length,
    gallerySignature,
  ]);

  useEffect(() => {
    setHasLoadedMainImage(
      false,
    );
  }, [
    safeActiveIndex,
    gallerySignature,
  ]);

  useEffect(() => {
    if (!canNavigate) {
      return;
    }

    const rail =
      thumbRailRef.current;

    const activeThumb =
      rail?.querySelector(
        `[data-thumb-index="${safeActiveIndex}"]`,
      );

    activeThumb?.scrollIntoView?.(
      {
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      },
    );
  }, [
    canNavigate,
    safeActiveIndex,
  ]);

  const handlePrev =
    useCallback(() => {
      if (!canNavigate) {
        return;
      }

      setActiveIndexSafely(
        safeActiveIndex === 0
          ? gallery.length -
              1
          : safeActiveIndex -
              1,
      );
    }, [
      canNavigate,
      gallery.length,
      safeActiveIndex,
      setActiveIndexSafely,
    ]);

  const handleNext =
    useCallback(() => {
      if (!canNavigate) {
        return;
      }

      setActiveIndexSafely(
        safeActiveIndex ===
          gallery.length - 1
          ? 0
          : safeActiveIndex +
              1,
      );
    }, [
      canNavigate,
      gallery.length,
      safeActiveIndex,
      setActiveIndexSafely,
    ]);

  return (
    <section
      className={clsx(
        "pdp-gallery",
        {
          "pdp-gallery--single":
            !canNavigate,
        },
      )}
      aria-label={`${
        productName ||
        "Product"
      } gallery`}
    >
      {canNavigate ? (
        <div
          className="pdp-gallery__thumbs"
          ref={thumbRailRef}
        >
          {gallery.map(
            (
              image,
              index,
            ) => (
              <button
                key={`${image.url}-${index}`}
                type="button"
                data-thumb-index={
                  index
                }
                className={clsx(
                  "pdp-gallery__thumb",
                  {
                    "is-active":
                      index ===
                      safeActiveIndex,
                  },
                )}
                onClick={() =>
                  setActiveIndexSafely(
                    index,
                  )
                }
                aria-label={`View image ${
                  index + 1
                }`}
                aria-current={
                  index ===
                  safeActiveIndex
                    ? "true"
                    : undefined
                }
              >
                <ProductImage
                  image={
                    image
                  }
                  fallback={
                    FALLBACK_IMAGE
                  }
                  alt={
                    image.alt ||
                    productName ||
                    `Product thumbnail ${
                      index +
                      1
                    }`
                  }
                  className="pdp-gallery__thumb-image"
                  pictureClassName="pdp-gallery__thumb-picture"
                  sizes="76px"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ),
          )}
        </div>
      ) : null}

      <div className="pdp-gallery__stage">
        <div className="pdp-gallery__toolbar">
          <span className="pdp-gallery__count">
            Image{" "}
            {safeActiveIndex +
              1}{" "}
            of{" "}
            {gallery.length}
          </span>

          <button
            type="button"
            className="pdp-gallery__expand"
            onClick={() =>
              setLightboxOpen(
                true,
              )
            }
            aria-label="Open larger image view"
          >
            <Expand
              size={15}
            />

            View larger
          </button>
        </div>

        <div className="pdp-gallery__frame">
          {!hasLoadedMainImage ? (
            <div
              className="pdp-gallery__loading-shimmer"
              aria-hidden="true"
            />
          ) : null}

          <div className="pdp-gallery__image-shell">
            <PremiumImage
              image={
                activeImage
              }
              alt={
                activeImage?.alt
              }
              caption={
                activeImage?.caption
              }
              productName={
                productName
              }
              onOpen={() =>
                setLightboxOpen(
                  true,
                )
              }
              onLoad={() =>
                setHasLoadedMainImage(
                  true,
                )
              }
              isPrimary={
                safeActiveIndex ===
                0
              }
            />
          </div>

          {canNavigate ? (
            <>
              <button
                type="button"
                className="pdp-gallery__nav pdp-gallery__nav--prev"
                onClick={
                  handlePrev
                }
                aria-label="Previous image"
              >
                <ChevronLeft
                  size={18}
                />
              </button>

              <button
                type="button"
                className="pdp-gallery__nav pdp-gallery__nav--next"
                onClick={
                  handleNext
                }
                aria-label="Next image"
              >
                <ChevronRight
                  size={18}
                />
              </button>
            </>
          ) : null}
        </div>
      </div>

      <Lightbox
        className="pdp-lightbox"
        open={
          lightboxOpen
        }
        close={() =>
          setLightboxOpen(
            false,
          )
        }
        slides={slides}
        index={
          safeActiveIndex
        }
        plugins={[Zoom]}
        carousel={{
          finite:
            gallery.length <=
            1,

          imageFit:
            "contain",
        }}
        zoom={{
          maxZoomPixelRatio:
            1,

          scrollToZoom:
            true,
        }}
        render={{
          buttonPrev:
            gallery.length <=
            1
              ? () => null
              : undefined,

          buttonNext:
            gallery.length <=
            1
              ? () => null
              : undefined,
        }}
      />
    </section>
  );
}
